import React, { createContext, useContext, useState } from "react";
import {
  IFoodItem,
  IFoodMenuCategory,
} from "../../../../../redux/food/food.types";

interface OrderContextType {
  selectedCategoryName: string;
  selectedCategory: IFoodMenuCategory | null;
  setSelectedCategory: (category: IFoodMenuCategory | null) => void;
  selectedItems: IFoodItem[];
  addItemToOrder: (item: IFoodItem) => void;
  removeItemFromOrder: (itemUuid: string) => void;
  updateItemQuantity: (itemUuid: string, quantity: number) => void;
  clearOrder: () => void;
  currentCategoryItems: IFoodItem[];
  setCurrentCategoryItems: (items: IFoodItem[]) => void;
  fetchedItems: IFoodItem[];
  setFetchedItems: (items: IFoodItem[]) => void;
  masterMenusFetchedItems: IFoodItem[];
  setMasterMenusFetchedItems: (items: IFoodItem[]) => void;
  removeFetchedItem: (itemUuid: string) => void;
  deletedItemUuids: Set<string>;
  addDeletedItemUuid: (uuid: string) => void;
  mergeItemsFromMultipleMenus: (itemsArrays: IFoodItem[][]) => IFoodItem[];
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const OrderProvider: React.FC<{
  children: React.ReactNode;
  leadUuid?: string;
}> = ({ children, leadUuid }) => {
  const [selectedCategory, setSelectedCategory] =
    useState<IFoodMenuCategory | null>(null);
  const [selectedItems, setSelectedItems] = useState<IFoodItem[]>([]);
  const [currentCategoryItems, setCurrentCategoryItems] = useState<IFoodItem[]>(
    []
  );
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>("");
  const [fetchedItems, setFetchedItems] = useState<IFoodItem[]>([]);
  const [masterMenusFetchedItems, setMasterMenusFetchedItems] = useState<
    IFoodItem[]
  >([]);
  const [deletedItemUuids, setDeletedItemUuids] = useState<Set<string>>(
    new Set()
  );

  const addDeletedItemUuid = (uuid: string) => {
    setDeletedItemUuids((prev) => {
      const newSet = new Set(prev);
      newSet.add(uuid);
      return newSet;
    });
  };

  const removeFetchedItem = (itemUuid: string) => {
    // addDeletedItemUuid(itemUuid);
    setFetchedItems((prev) =>
      prev.filter((item) => item.food_item_uuid !== itemUuid)
    );
    setMasterMenusFetchedItems((prev) =>
      prev.filter((item) => item.food_item_uuid !== itemUuid)
    );
  };

  const handleSetSelectedCategory = (category: IFoodMenuCategory | null) => {
    setSelectedCategory(category);
    setSelectedCategoryName(category?.category_name || "");
    setCurrentCategoryItems(category?.food_items || []);
  };

  const addItemToOrder = (item: IFoodItem) => {
    // Add the current category to the item when adding to order
    const itemWithCategory = {
      ...item,
      // Ensure we maintain the category_list structure
      category_list: selectedCategory ? [selectedCategory] : [],
    };
    setSelectedItems((prevItems) => [...prevItems, itemWithCategory]);
  };

  const removeItemFromOrder = (itemUuid: string) => {
    setSelectedItems((prevItems) =>
      prevItems.filter((item) => item.food_item_uuid !== itemUuid)
    );
  };

  const updateItemQuantity = (itemUuid: string, quantity: number) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.food_item_uuid === itemUuid ? { ...item, quantity } : item
      )
    );
  };

  const clearOrder = () => {
    setSelectedItems([]);
  };

  const mergeItemsFromMultipleMenus = (itemsArrays: IFoodItem[][]) => {
    const mergedItems: IFoodItem[] = [];
    const seenUuids = new Set<string>();

    itemsArrays.forEach((items) => {
      items.forEach((item) => {
        if (item.food_item_uuid && !seenUuids.has(item.food_item_uuid)) {
          seenUuids.add(item.food_item_uuid);
          mergedItems.push(item);
        }
      });
    });

    return mergedItems;
  };

  React.useEffect(() => {
    if (leadUuid) {
      // Reset relevant states when leadUuid changes
      setFetchedItems([]);
      setSelectedItems([]);
      setSelectedCategory(null);
      setCurrentCategoryItems([]);
      setDeletedItemUuids(new Set());
    }
  }, [leadUuid]);

  return (
    <OrderContext.Provider
      value={{
        selectedCategoryName,
        selectedCategory,
        setSelectedCategory: handleSetSelectedCategory,

        selectedItems,
        addItemToOrder,
        removeItemFromOrder,
        updateItemQuantity,
        clearOrder,
        currentCategoryItems,
        setCurrentCategoryItems,
        fetchedItems,
        setFetchedItems,
        // setFetchedItems: setFetchedItemsFiltered,
        masterMenusFetchedItems,
        setMasterMenusFetchedItems,
        // setMasterMenusFetchedItems: setMasterMenusFetchedItemsFiltered,
        removeFetchedItem,
        deletedItemUuids,
        addDeletedItemUuid,
        mergeItemsFromMultipleMenus,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrderContext = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrderContext must be used within an OrderProvider");
  }
  return context;
};
